import React from 'react';
import { Outlet } from 'react-router-dom';
import { CatalogProvider } from '@repair/catalog';
import { useSelector } from 'react-redux';

import { selectConfiguration, selectIsProd } from '../store/selectors/configuration';
import { selectLocaleToString } from '../store/selectors/i18n.selectors';
import { historyRouter } from './historyRouter';
import { CatalogProductSheetNavigator } from '../components/ToolbarTop/Header/CatalogProductSheetNavigator';
import { useAlgoliaCredentialsQuery } from '../features/algolia/queries/useAlgoliaCredentialsQuery';

const FALLBACK_ALGOLIA_CREDENTIAL = 'unavailable';

interface CatalogWrapperProps {
  readonly children?: React.ReactNode;
}

export const CatalogWrapper = ({ children }: CatalogWrapperProps) => {
  const locale = useSelector(selectLocaleToString);
  const isProd = useSelector(selectIsProd);
  const configuration = useSelector(selectConfiguration);
  const { data: algoliaCredentials, isError } = useAlgoliaCredentialsQuery();

  const handleUnauthorized = async (response: Response) => {
    const { authUrl } = await response.json();

    if (!authUrl) {
      throw new Error('Unable to refresh access token. Please refresh the page and try again.');
    }

    globalThis.location.replace(authUrl);
  };

  if (!algoliaCredentials && !isError) {
    return null;
  }

  return (
    <CatalogProvider
      algoliaConfig={{
        appId: algoliaCredentials?.applicationId ?? FALLBACK_ALGOLIA_CREDENTIAL,
        searchKey: algoliaCredentials?.apiKey ?? FALLBACK_ALGOLIA_CREDENTIAL,
      }}
      catalogConfig={{
        auth: { type: 'cookie' },
        onUnauthorized: handleUnauthorized,
        locale,
        domainApi: configuration.config.services.ICARE_BACK.url,
        mode: 'proxy',
        env: isProd ? 'production' : 'iso',
      }}
      historyRouter={historyRouter}
    >
      <CatalogProductSheetNavigator />
      {children ?? <Outlet />}
    </CatalogProvider>
  );
};
